import React, { useRef, useCallback, useImperativeHandle, forwardRef } from "react";
import { IScrollInfo } from "./types";
import { debounce } from "@common/utils/common";
import { useLazyListContext } from "./LazyListContext";

interface LazyListProps {
  children: React.ReactNode[];
  onScroll?: (scrollInfo: IScrollInfo) => void;
  className?: string;
}

const LazyList = forwardRef<HTMLDivElement, LazyListProps>(({
  children,
  onScroll,
  className,
}, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { state, dispatch, visibleIndicesRef } = useLazyListContext();
  const { itemPositionMap } = state;

  useImperativeHandle(ref, () => containerRef.current as HTMLDivElement);

  const handleVisibilityChange = useCallback(
    (index: number, isVisible: boolean) => {
      const newSet = new Set(visibleIndicesRef.current);
      if (isVisible) {
        newSet.add(index);
      } else {
        newSet.delete(index);
      }
      dispatch({ type: "updateVisibleIndices", payload: newSet });
    },
    [dispatch, visibleIndicesRef]
  );

  const debounceHandleScroll = debounce(() => {
    const container = containerRef.current;
    if (!container) return;
    onScroll &&
      onScroll({
        scrollTop: container.scrollTop,
        scrollHeight: container.scrollHeight,
        clientHeight: container.clientHeight,
      });

    const containerTop = container.scrollTop;
    const containerBottom = containerTop + container.clientHeight;
    Object.values(itemPositionMap).forEach((position, index) => {
      const inViewport =
        position.top < containerBottom &&
        position.top + position.height > containerTop;
      handleVisibilityChange(index, inViewport);
    });
  }, 500);

  return (
    <div
      ref={containerRef}
      className={className}
      onScroll={debounceHandleScroll}
    >
      {children}
    </div>
  );
});

export default LazyList;